import { createDateFormatter, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { ArrowUpRight, CalendarDays, ShieldCheck } from "lucide-react";
import { firstError, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { Carousel, eisColor } from "@nexus/widget-kit";
import { PROTECTION_OVERVIEW_CAMPAIGNS_CONTRACT } from "./types";
import type { ProtectionOverviewCampaignEntry, ProtectionOverviewCampaignsData } from "./types";

export interface ProtectionOverviewCampaignsConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "protection-overview-campaigns",
  name: "Protection Overview - Campaigns",
  description: "One tenant's top campaigns for one report edition, with drill-down.",
  version: "1.0.0",
  dataRequests: {
    campaigns: {
      source: "protection-overview-tenant-campaigns",
      contract: PROTECTION_OVERVIEW_CAMPAIGNS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: ProtectionOverviewCampaignsConfig = {};

function formatRange(start: string, end: string, timezone: TimezoneMode): string {
  const dateFormat = createDateFormatter(timezone, { day: "2-digit", month: "short" }, "en");
  return `${dateFormat.format(new Date(start))} - ${dateFormat.format(new Date(end))}`;
}

function CampaignCard({
  campaign,
  onOpen,
}: {
  campaign: ProtectionOverviewCampaignEntry;
  onOpen: (campaignId: string) => void;
}) {
  const color = eisColor(campaign.avgIntegrity);
  const timezone = useDisplayTimezone();

  return (
    <button
      type="button"
      disabled={!campaign.hasReport}
      onClick={() => onOpen(campaign.id)}
      className="group flex w-72 shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface p-4 text-left transition-colors enabled:hover:border-brand-cyan/40 enabled:hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-primary">{campaign.label}</span>
        {campaign.hasReport ? (
          <ArrowUpRight className="h-4 w-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-brand-cyan" />
        ) : null}
      </div>
      <div className="flex items-center gap-1.5 text-2xs text-muted">
        <CalendarDays className="h-3.5 w-3.5" />
        {formatRange(campaign.start, campaign.end, timezone)}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-2">
        <div className="flex flex-col">
          <span className="text-2xl font-semibold tabular-nums" style={{ color }}>
            {campaign.avgIntegrity}
          </span>
          <span className="text-xs text-muted">Average EIS</span>
        </div>
        <span className="flex items-center gap-1 text-2xs text-muted">
          <ShieldCheck className="h-3.5 w-3.5" />
          {campaign.eventCount} protected events
        </span>
      </div>
      {!campaign.hasReport && (
        <span className="text-2xs text-muted" role="status">
          No detailed report in this edition
        </span>
      )}
    </button>
  );
}

export function Component({
  data,
  config,
  dashboard,
}: WidgetProps<ProtectionOverviewCampaignsConfig>) {
  const error = firstError(data, ["campaigns"]);
  const result = unwrap<ProtectionOverviewCampaignsData>(data.campaigns);
  const loading = data.campaigns?.isLoading && !result;
  const heading = config.title ?? (result ? `Campaigns - ${result.tenant.label}` : manifest.name);

  const onSelectCampaign = (campaignId: string) => {
    dashboard.emitInteraction?.({ type: "campaign.select", payload: { campaignId } });
  };

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="cards" />
      ) : result.campaigns.length === 0 ? (
        <WidgetState title="No campaigns for this tenant in this edition." />
      ) : (
        <div className="p-4">
          <Carousel>
            {result.campaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} onOpen={onSelectCampaign} />
            ))}
          </Carousel>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<ProtectionOverviewCampaignsConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
